import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { SiteInfo } from '../../views/CalendarMultiView'
import type { NavFn } from '../../pages/SchedulerPage'
import type { Partner, ScheduleDetailTask } from '../../types/gantt.types'
import { SigongPopup, type SigongTask } from '../../views/SigongPopup'
import { MaterialOrderPopup } from '../../views/MaterialOrderPopup'
import { AddTaskPopup } from '../../views/AddTaskPopup'
import { WORK_TYPE_COLORS } from '../../views/scheduleUtils'
import { ShipStatusPopup } from '../ShipStatusPopup'
import { FieldReportStatusPopup } from '../FieldReportStatusPopup'
import { SCHEDULE_LIST_KEY } from '../../hooks/useScheduleListData'
import type { ListPopup } from '../../hooks/useScheduleListState'

// 팝업 호스트(D-10) — 시공파트너배정·자재파트너배정·배송정보현황·시공현황·공종 추가.
// GanttPopupHost 는 간트 상태에 묶여 있어 수정하지 않고 리스트용으로 새로 둔다. 열림 상태는 useScheduleListState(C-02)가 가진다.
// 변경(배정·발송·저장) 뒤에는 afterChange(useScheduleListActions)로 신호·공종·발주 쿼리를 무효화한다(낙관 갱신 없음).

export interface ScheduleListPopupHostProps {
  site: SiteInfo
  /** 공종 상세 응답의 tasks */
  tasks: readonly ScheduleDetailTask[]
  popup: ListPopup | null
  onClose: () => void
  /** 다른 팝업을 연다(배송정보현황 → 시공파트너배정) */
  openPopup: (p: ListPopup) => void
  /** 변경(배정·발송·저장) 뒤 재조회(useScheduleListActions.afterChange) */
  afterChange: () => unknown
  navigate: NavFn
  embedded?: boolean
}

function usePartners(enabled: boolean) {
  const user = useAuthStore(s => s.user)
  const headers = useMemo(() => ({
    ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }), [user?.compId, user?.memberId])
  return useQuery<Partner[]>({
    queryKey: [SCHEDULE_LIST_KEY, 'partners', user?.compId ?? null],
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<{ data: Partner[] }>('/schedule/partners', { headers, signal })
      return res.data?.data ?? []
    },
    enabled: enabled && !!user?.compId,
  })
}

export function ScheduleListPopupHost({ site, tasks, popup, onClose, openPopup, afterChange, navigate, embedded }: ScheduleListPopupHostProps) {
  const qc = useQueryClient()
  const needPartners = popup?.kind === 'sigong' || popup?.kind === 'addTask'
  const partnersQ = usePartners(needPartners)
  const partners = partnersQ.data ?? []

  const sigongTasks = useMemo<SigongTask[]>(() => tasks.map((t, i) => ({
    taskId: t.taskId,
    workTypeCode: t.workTypeCode,
    workTypeName: t.workTypeName,
    startDate: new Date(t.startDate),
    endDate: new Date(t.endDate),
    color: WORK_TYPE_COLORS[i % WORK_TYPE_COLORS.length],
    foremanName: t.foremanName || null,
    foremanContact: t.foremanContact || null,
    partnerStatus: t.partnerStatus,
    respondedDtm: t.respondedDtm,
    sentDtm: t.sentDtm,
    assigneePartnerId: t.assigneePartnerId,
    assignSource: t.assignSource ?? null,
    constructByVendor: t.constructByVendorYn === 'Y',
  })), [tasks])

  const taskNameById = useMemo(() => new Map(tasks.map(t => [t.taskId, t.workTypeName] as const)), [tasks])

  if (!popup) return null
  const scheduleId = site.scheduleId

  switch (popup.kind) {
    case 'sigong':
      return (
        <SigongPopup
          tasks={sigongTasks}
          partners={partners}
          siteName={site.siteName}
          scheduleId={scheduleId ?? undefined}
          onClose={onClose}
          onPartnerCreated={() => { void qc.invalidateQueries({ queryKey: [SCHEDULE_LIST_KEY, 'partners'] }) }}
          onAssigned={() => { void afterChange() }}
          onOpenSettings={embedded ? undefined : () => navigate('settings-foreman-confirm')}
        />
      )
    case 'material':
      if (scheduleId == null) return null
      return (
        <MaterialOrderPopup
          siteId={site.siteId}
          scheduleId={scheduleId}
          siteName={site.siteName}
          focusTaskId={popup.focusTaskId}
          onClose={onClose}
          onChanged={() => { void afterChange() }}
          onOpenOrderSettings={embedded ? undefined : () => navigate('settings-order')}
        />
      )
    case 'ship':
      if (scheduleId == null) return null
      return (
        <ShipStatusPopup
          onClose={() => { onClose(); void afterChange() }}
          scheduleId={scheduleId}
          siteName={site.siteName}
          focusTaskId={popup.focusTaskId}
          taskNameById={taskNameById}
          vendorConstructTaskIds={tasks.filter(t => t.constructByVendorYn === 'Y').map(t => t.taskId)}
          onOpenForemanAssign={() => openPopup({ kind: 'sigong' })}
        />
      )
    case 'status':
      return <FieldReportStatusPopup onClose={onClose} siteId={site.siteId} siteName={site.siteName} />
    case 'addTask':
      return (
        <AddTaskPopup
          onClose={onClose}
          selectedSite={site}
          partners={partners}
          existingWorkTypeCodes={tasks.map(t => t.workTypeCode)}
          onSaved={() => { void afterChange() }}
        />
      )
  }
}
